import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useRef, useCallback, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { useId } from "@reach/auto-id";
import { useKeyboardShortcut } from "../../hooks/use-keyboard-shortcut.es.js";
import "styled-components";
import "lodash/fp";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import "@reach/utils";
import "@reach/rect";
import "compute-scroll-into-view";
import { LabelText } from "../label-text/label-text.es.js";
import { Message } from "../message/message.es.js";
import { Pill } from "../pill/pill.es.js";
import { Stack } from "../stack/stack.es.js";
import { InputBox, InputInner, InputImpl, InputAuxillary } from "./input.styles.es.js";
import { UtilityButton } from "../utility-button/utility-button.es.js";
import { inputAdjoin } from "./input.utils.es.js";
import { isControlCharacter } from "../../utils/is-control-character.es.js";
const propTypes = {
  adjoin: PropTypes.oneOf(["both", "left", "none", "right"]),
  appearance: PropTypes.oneOf(["bare", "default"]),
  disabled: PropTypes.bool,
  expanded: PropTypes.bool,
  keyboardShortcut: PropTypes.func,
  label: PropTypes.string,
  labelId: PropTypes.string,
  magnitude: PropTypes.oneOf(["small", "medium", "large", "xlarge"]),
  maxWidth: PropTypes.number,
  message: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  prefix: PropTypes.node,
  role: PropTypes.string,
  size: PropTypes.number,
  suffix: PropTypes.node,
  textAlign: PropTypes.oneOf(["center", "left", "right"]),
  tone: PropTypes.oneOf(["critical", "neutral", "positive"]),
  type: PropTypes.oneOf(["email", "password", "search", "tel", "text", "url"]),
  value: PropTypes.string
};
const defaultProps = {
  adjoin: void 0,
  appearance: "default",
  disabled: false,
  expanded: false,
  keyboardShortcut: void 0,
  label: void 0,
  labelId: void 0,
  magnitude: "small",
  message: void 0,
  maxWidth: void 0,
  onChange: void 0,
  onKeyDown: void 0,
  placeholder: void 0,
  prefix: void 0,
  role: void 0,
  size: 1e3,
  suffix: void 0,
  textAlign: "left",
  tone: "neutral",
  type: "text",
  value: void 0
};
const Input = forwardRef(
  ({
    adjoin,
    appearance,
    disabled,
    expanded,
    keyboardShortcut,
    label,
    labelId,
    magnitude,
    maxWidth,
    message,
    onChange,
    onKeyDown,
    placeholder,
    prefix,
    role,
    size,
    suffix,
    textAlign,
    tone,
    type,
    value,
    ...restProps
  }, forwardedRef) => {
    const id = `input__${useId()}`;
    const ref = useRef();
    const cancellable = type === "search" && Boolean(value);
    const keyboardRef = useKeyboardShortcut({
      predicate: keyboardShortcut,
      callback: (event) => {
        event.preventDefault();
        ref.current.focus();
      }
    });
    const handleChange = useCallback(
      (event) => {
        if (onChange) {
          onChange(event.currentTarget.value);
        }
      },
      [onChange]
    );
    const handleClear = useCallback(() => {
      ref.current.focus();
      if (onChange) {
        onChange("");
      }
    }, [onChange]);
    const handleKeyDown = useCallback(
      (event) => {
        if (!event.ctrlKey && !event.metaKey && !isControlCharacter(event.key)) {
          event.stopPropagation();
        }
        if (onKeyDown) {
          onKeyDown(event);
        }
      },
      [onKeyDown]
    );
    useImperativeHandle(forwardedRef, () => ref.current);
    useImperativeHandle(keyboardRef, () => ref.current);
    return /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
      label && /* @__PURE__ */ jsx(LabelText, { as: "label", htmlFor: id, id: labelId, children: label }),
      /* @__PURE__ */ jsxs(InputBox, { children: [
        prefix && /* @__PURE__ */ jsx(Pill, { adjoin: "left", magnitude: "medium", children: prefix }),
        /* @__PURE__ */ jsxs(InputInner, { maxWidth, children: [
          /* @__PURE__ */ jsx(
            InputImpl,
            {
              ref,
              "aria-describedby": message ? `${id}__message` : void 0,
              "aria-expanded": expanded || void 0,
              disabled,
              id,
              onChange: handleChange,
              onKeyDown: handleKeyDown,
              placeholder,
              role,
              size,
              type,
              value,
              $adjoin: adjoin || inputAdjoin({ prefix, suffix }),
              $appearance: appearance,
              $cancellable: cancellable,
              $magnitude: magnitude,
              $textAlign: textAlign,
              $tone: tone,
              ...restProps
            }
          ),
          cancellable && /* @__PURE__ */ jsx(InputAuxillary, { $magnitude: magnitude, children: /* @__PURE__ */ jsx(
            UtilityButton,
            {
              as: "i",
              icon: "close",
              magnitude: magnitude === "xlarge" ? "small" : "xsmall",
              onClick: handleClear,
              stopPropagation: true,
              tabIndex: -1
            }
          ) })
        ] }),
        suffix && /* @__PURE__ */ jsx(Pill, { adjoin: "right", magnitude: "medium", children: suffix })
      ] }),
      message && /* @__PURE__ */ jsx(Message, { id: `${id}__message`, tone, children: message })
    ] });
  }
);
Input.propTypes = propTypes;
Input.defaultProps = defaultProps;
export {
  Input
};
